import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { getMovieDetails, getTvShowDetails, getMovieRecommendations, getTvRecommendations } from '../services/tmdbService';
import { addToRecentlyViewed } from '../utils/recentlyViewed';
import DetailHeader from './Others/DetailHeader';
import '../styles/MovieDetails.css';
import '../styles/Player.css';

// Multiple embed providers — when one breaks, users can flip to another.
// Order = preference. The first one is the default.
const SOURCES = [
  {
    id: 'vidsrc-cc',
    label: 'Server 1',
    movie: (id) => `https://vidsrc.cc/v2/embed/movie/${encodeURIComponent(id)}?autoPlay=true`,
    tv: (id, s, e) => `https://vidsrc.cc/v2/embed/tv/${encodeURIComponent(id)}/${encodeURIComponent(s)}/${encodeURIComponent(e)}?autoPlay=true`,
  },
  {
    id: 'vidsrc-to',
    label: 'Server 2',
    movie: (id) => `https://vidsrc.to/embed/movie/${encodeURIComponent(id)}`,
    tv: (id, s, e) => `https://vidsrc.to/embed/tv/${encodeURIComponent(id)}/${encodeURIComponent(s)}/${encodeURIComponent(e)}`,
  },
  {
    id: 'multiembed',
    label: 'Server 3',
    movie: (id) => `https://multiembed.mov/?video_id=${encodeURIComponent(id)}&tmdb=1`,
    tv: (id, s, e) => `https://multiembed.mov/?video_id=${encodeURIComponent(id)}&tmdb=1&s=${encodeURIComponent(s)}&e=${encodeURIComponent(e)}`,
  },
  {
    id: '2embed',
    label: 'Server 4',
    movie: (id) => `https://www.2embed.cc/embed/${encodeURIComponent(id)}`,
    tv: (id, s, e) => `https://www.2embed.cc/embedtv/${encodeURIComponent(id)}&s=${encodeURIComponent(s)}&e=${encodeURIComponent(e)}`,
  },
  {
    id: 'vidlink',
    label: 'Server 5',
    movie: (id) => `https://vidlink.pro/movie/${encodeURIComponent(id)}`,
    tv: (id, s, e) => `https://vidlink.pro/tv/${encodeURIComponent(id)}/${encodeURIComponent(s)}/${encodeURIComponent(e)}`,
  },
  {
    id: 'vidsrc-icu',
    label: 'Server 6',
    movie: (id) => `https://vidsrc.icu/embed/movie/${encodeURIComponent(id)}`,
    tv: (id, s, e) => `https://vidsrc.icu/embed/tv/${encodeURIComponent(id)}/${encodeURIComponent(s)}/${encodeURIComponent(e)}`,
  },
];

const SOURCE_KEY = 'viewly_preferred_source';

const Player = () => {
  const { id: routeId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(search);
  const id = queryParams.get('id') || routeId || '';
  const initialSeason = parseInt(queryParams.get('s')) || 1;
  const initialEpisode = parseInt(queryParams.get('e')) || 1;

  const [relatedContent, setRelatedContent] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(initialSeason);
  const [currentEpisode, setCurrentEpisode] = useState(initialEpisode);
  const [tvShowData, setTvShowData] = useState(null);
  const [showTitle, setShowTitle] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sourceId, setSourceId] = useState(() => {
    try {
      const stored = localStorage.getItem(SOURCE_KEY);
      if (stored && SOURCES.some((s) => s.id === stored)) return stored;
    } catch { /* ignore */ }
    return SOURCES[0].id;
  });

  const isTvShow = queryParams.get('s') && queryParams.get('e');
  const mediaType = isTvShow ? 'tv' : 'movie';

  const playerRef = React.useRef(null);

  const activeSource = SOURCES.find((s) => s.id === sourceId) || SOURCES[0];

  const embedUrl = isTvShow
    ? activeSource.tv(id, currentSeason, currentEpisode)
    : activeSource.movie(id);

  const detailsUrl = isTvShow ? `/tv/${id}` : `/movie/${id}`;

  const switchSource = (nextId) => {
    setSourceId(nextId);
    try { localStorage.setItem(SOURCE_KEY, nextId); } catch { /* ignore */ }
  };

  useEffect(() => {
    const fetchDetailsAndTrack = async () => {
      try {
        // Fetch details to get title/name and poster
        const details = isTvShow
          ? await getTvShowDetails(id)
          : await getMovieDetails(id);

        if (isTvShow) {
          setTvShowData(details);
          setShowTitle(details.name);
        }

        // Track to recently viewed
        addToRecentlyViewed({
          id: parseInt(id),
          title: details.title || details.name,
          poster_path: details.poster_path,
          type: mediaType,
          timestamp: Date.now()
        });

        // Fetch related content
        const recommendations = isTvShow
          ? await getTvRecommendations(id)
          : await getMovieRecommendations(id);

        setRelatedContent((recommendations?.results || []).slice(0, 6));
      } catch (error) {
        console.error('Error fetching player data:', error);
      }
    };

    fetchDetailsAndTrack();
  }, [id, isTvShow, mediaType]);

  // Update URL when season/episode changes
  useEffect(() => {
    if (isTvShow) {
      const newUrl = `/player/${id}?s=${currentSeason}&e=${currentEpisode}`;
      navigate(newUrl, { replace: true });
    }
  }, [currentSeason, currentEpisode, id, isTvShow, navigate]);

  // Episode navigation functions
  const goToNextEpisode = () => {
    if (!tvShowData) return;

    const currentSeasonData = tvShowData.seasons.find(s => s.season_number === currentSeason);
    if (!currentSeasonData) return;

    const totalEpisodesInSeason = currentSeasonData.episode_count || 0;

    if (currentEpisode < totalEpisodesInSeason) {
      setCurrentEpisode(currentEpisode + 1);
    } else {
      const nextSeasonData = tvShowData.seasons.find(s => s.season_number === currentSeason + 1);
      if (nextSeasonData && (nextSeasonData.episode_count || 0) > 0) {
        setCurrentSeason(currentSeason + 1);
        setCurrentEpisode(1);
      }
    }
  };

  const goToPreviousEpisode = () => {
    if (!tvShowData) return;

    if (currentEpisode > 1) {
      setCurrentEpisode(currentEpisode - 1);
    } else if (currentSeason > 1) {
      const previousSeasonData = tvShowData.seasons.find(s => s.season_number === currentSeason - 1);
      if (previousSeasonData && (previousSeasonData.episode_count || 0) > 0) {
        setCurrentSeason(currentSeason - 1);
        setCurrentEpisode(previousSeasonData.episode_count);
      }
    }
  };

  const handleSeasonChange = (e) => {
    const newSeason = parseInt(e.target.value);
    setCurrentSeason(newSeason);
    setCurrentEpisode(1); // Reset to episode 1 when changing seasons
  };

  const handleEpisodeChange = (e) => {
    setCurrentEpisode(parseInt(e.target.value));
  };

  // Check if navigation buttons should be disabled
  const isFirstEpisode = currentSeason === 1 && currentEpisode === 1;
  const isLastEpisode = tvShowData && tvShowData.seasons.length > 0 && (() => {
    const lastSeason = tvShowData.seasons[tvShowData.seasons.length - 1];
    return currentSeason === lastSeason.season_number &&
           currentEpisode === (lastSeason.episode_count || 0);
  })();

  // Fullscreen functionality
  const toggleFullscreen = () => {
    if (!playerRef.current) return;

    if (!document.fullscreenElement) {
      // Enter fullscreen
      if (playerRef.current.requestFullscreen) {
        playerRef.current.requestFullscreen();
      } else if (playerRef.current.webkitRequestFullscreen) {
        playerRef.current.webkitRequestFullscreen();
      } else if (playerRef.current.mozRequestFullScreen) {
        playerRef.current.mozRequestFullScreen();
      } else if (playerRef.current.msRequestFullscreen) {
        playerRef.current.msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  return (
    <div className="player-page">
      <DetailHeader />

      {/* Player Section */}
      <div className="player" ref={playerRef}>
        <iframe
          key={`${sourceId}-${currentSeason}-${currentEpisode}`}
          title="player"
          src={embedUrl}
          frameBorder="0"
          allowFullScreen={true}
          webkitallowfullscreen="true"
          mozallowfullscreen="true"
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture; accelerometer; gyroscope; clipboard-write; web-share"
          referrerPolicy="origin"
        ></iframe>

        <div className="player-overlay">
          <div className="player-controls">
            <Link to="/" className="player-btn" aria-label="Go to home">
              <i className="fa-solid fa-home"></i>
            </Link>

            <Link to={detailsUrl} className="player-btn" aria-label="View details">
              <i className="fa-solid fa-info-circle"></i>
            </Link>

            <button
              onClick={toggleFullscreen}
              className="player-btn"
              aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              <i className={`fa-solid ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Source switcher — flip between embed providers if one isn't playing */}
      <div className="player-sources" role="tablist" aria-label="Video source">
        <span className="player-sources-label">If video isn&apos;t playing, try another source:</span>
        <div className="player-sources-row">
          {SOURCES.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={s.id === sourceId}
              className={`player-source-btn ${s.id === sourceId ? 'active' : ''}`}
              onClick={() => switchSource(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Episode Navigation Controls (TV Shows Only) */}
      {isTvShow && tvShowData && (
        <div className="episode-controls">
          <div className="episode-header">
            <h2 className="show-title">{showTitle}</h2>
            <span className="episode-badge">
              S{currentSeason} E{currentEpisode}
            </span>
          </div>

          <div className="episode-navigation">
            <button
              className="ep-nav-btn prev"
              onClick={goToPreviousEpisode}
              disabled={isFirstEpisode}
              aria-label="Previous episode"
            >
              <i className="fa-solid fa-backward-step"></i>
            </button>

            <div className="episode-selectors">
              <select
                id="seasonSelect"
                value={currentSeason}
                onChange={handleSeasonChange}
                aria-label="Select season"
              >
                {tvShowData.seasons
                  .filter(season => season.season_number > 0)
                  .map(season => (
                    <option key={season.season_number} value={season.season_number}>
                      Season {season.season_number}
                    </option>
                  ))}
              </select>

              <select
                id="episodeSelect"
                value={currentEpisode}
                onChange={handleEpisodeChange}
                aria-label="Select episode"
              >
                {Array.from(
                  { length: tvShowData.seasons.find(s => s.season_number === currentSeason)?.episode_count || 0 },
                  (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      Episode {i + 1}
                    </option>
                  )
                )}
              </select>
            </div>

            <button
              className="ep-nav-btn next"
              onClick={goToNextEpisode}
              disabled={isLastEpisode}
              aria-label="Next episode"
            >
              <i className="fa-solid fa-forward-step"></i>
            </button>
          </div>
        </div>
      )}

      {/* Related Content Section */}
      {relatedContent.length > 0 && (
        <section className="player-related">
          <div className="container">
            <h2>More Like This</h2>
            <div className="related-grid">
              {relatedContent.map(item => (
                <Link
                  to={isTvShow ? `/tv/${item.id}` : `/movie/${item.id}`}
                  key={item.id}
                  className="related-card"
                >
                  <img
                    src={`https://image.tmdb.org/t/p/w300${item.poster_path}`}
                    alt={item.title || item.name}
                    loading="lazy"
                  />
                  <p>{item.title || item.name}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Player;
